import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { message } from 'antd';
import { API_BASE_URL } from '../config';
import { useAuthStore } from '../stores/authStore';
import type { ApiResponse, ErrorResponse, PaginatedApiResponse } from '../types/api';

// 创建axios实例
const request: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 是否正在刷新token
let isRefreshing = false;
// 等待token刷新的请求队列
let refreshSubscribers: Array<(token: string) => void> = [];

// 订阅token刷新
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// 通知所有订阅者新token
function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 禁用 GET 请求缓存，防止浏览器返回旧数据
    if (config.method?.toLowerCase() === 'get' && config.headers) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 处理 204 No Content 响应（DELETE 操作等）
    if (response.status === 204) {
      return response;
    }

    const { data } = response;

    // 业务成功
    if (data && data.code === 0) {
      return response;
    }

    // 业务错误
    handleBusinessError(data as unknown as ErrorResponse);
    return Promise.reject(data || response);
  },
  async (error: AxiosError<ErrorResponse>) => {
    const { response, config } = error;
    
    if (!response) {
      message.error('网络错误，请检查网络连接');
      return Promise.reject(error);
    }
    
    const { status, data } = response;
    
    // 401: Token过期，尝试刷新
    if (status === 401) {
      const refreshToken = useAuthStore.getState().refreshToken;
      
      if (!refreshToken) {
        // 没有refresh token，跳转登录
        handleAuthError();
        return Promise.reject(error);
      }
      
      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          const res = await axios.post<ApiResponse<{ tokens: { access_token: string; refresh_token: string } }>>(
            `${API_BASE_URL}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );
          
          if (res.data.code === 0) {
            const { access_token, refresh_token } = res.data.data.tokens;
            
            // 更新 authStore 中的 token
            useAuthStore.setState({
              token: access_token,
              refreshToken: refresh_token,
            });
            
            onTokenRefreshed(access_token);
            isRefreshing = false;
            
            // 重试原请求
            if (config) {
              config.headers.Authorization = `Bearer ${access_token}`;
              return request(config);
            }
          } else {
            throw new Error('刷新token失败');
          }
        } catch (refreshError) {
          isRefreshing = false;
          handleAuthError();
          return Promise.reject(refreshError);
        }
      } else {
        // 等待token刷新完成
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            if (config) {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(request(config));
            }
          });
        });
      }
    }
    
    // 403: 权限不足
    if (status === 403) {
      message.error('权限不足，无法访问该资源');
    }
    
    // 404: 资源不存在
    if (status === 404) {
      message.error('请求的资源不存在');
    }
    
    // 429: 请求频率超限
    if (status === 429) {
      message.error('请求过于频繁，请稍后再试');
    }
    
    // 500+: 服务器错误
    if (status >= 500) {
      message.error('服务器错误，请稍后重试');
    }
    
    // 处理业务错误码
    if (data) {
      handleBusinessError(data);
    }
    
    return Promise.reject(error);
  }
);

// 处理业务错误
function handleBusinessError(error: ErrorResponse | undefined | null) {
  if (!error) {
    return;
  }

  const { code, message: msg, details } = error;
  
  // 根据错误码显示不同提示
  switch (code) {
    case 10001:
      message.error('系统内部错误');
      break;
    case 10101:
    case 10102:
    case 10103:
      // 参数错误，显示详细错误信息
      if (details && details.length > 0) {
        const firstError = details[0];
        message.error(`${firstError.field}: ${firstError.message}`);
      } else {
        message.error(msg || '参数错误');
      }
      break;
    case 10201:
      handleAuthError();
      break;
    case 10202:
      message.error('权限不足');
      break;
    case 10203:
      message.error('租户已冻结，请联系管理员');
      break;
    case 10204:
      message.error('租户订阅已过期，请续费');
      break;
    case 20104:
      message.error('用量额度已用完，请升级订阅');
      break;
    case 20105:
      message.error('用户席位数超限');
      break;
    default:
      message.error(msg || '操作失败');
  }
}

// 处理认证错误
function handleAuthError() {
  useAuthStore.getState().clearAuth();
  message.error('登录已过期，请重新登录');
  
  // 延迟跳转，让用户看到提示
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
}

// 封装请求方法
export const http = {
  get: <T>(url: string, params?: Record<string, unknown> | unknown, config?: { responseType?: string }) => {
    const requestConfig = config?.responseType ? { responseType: config.responseType as 'blob' | 'json' | undefined, params: params as Record<string, unknown> } : { params: params as Record<string, unknown> };
    return request.get<ApiResponse<T>>(url, requestConfig).then((res) => res.data);
  },

  // 分页请求：后端返回 SuccessWithPagination 格式 { code, message, data: T[], pagination }
  getPaginated: <T>(url: string, params?: Record<string, unknown> | unknown) => {
    return request
      .get<PaginatedApiResponse<T>>(url, { params: params as Record<string, unknown> })
      .then((res) => res.data);
  },

  post: <T>(url: string, data?: unknown) =>
    request.post<ApiResponse<T>>(url, data).then((res) => res.data),
    
  put: <T>(url: string, data?: unknown) =>
    request.put<ApiResponse<T>>(url, data).then((res) => res.data),
    
  patch: <T>(url: string, data?: unknown) =>
    request.patch<ApiResponse<T>>(url, data).then((res) => res.data),
    
  delete: <T>(url: string) =>
    request.delete<ApiResponse<T>>(url).then((res) => {
      if (res.status === 204) {
        return null as unknown as ApiResponse<T>;
      }
      return res.data;
    }),
};

export default request;
